import React from 'react';
import { useTranslation } from 'react-i18next';

import {
    curtainActuators,
    dimmingActuators,
    hvacActuators,
    switchingActuators 
} from '../../actuators';
import LeftMenu from '../../atoms/LeftMenu/LeftMenu';
import { detectors } from '../../detectors';
import { toggleLeftMenu } from '../../redux/reducers/appReducer';
import { useAppDispatch } from '../../redux/store';

import styles from '../../atoms/LeftMenu/leftMenu.module.scss';

interface MenuCategory {
    icon: string,
    title: string,
    items: CategoryItem[]
}

export interface CategoryItem {
    title: string,
    icon: string,
    type?: string,
    items?: CategoryItem[]
}

const LeftMenuComponent: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [leftMenuOpen, setLeftMenuOpen] = React.useState(false);

    const handleMenuClick = () => {
        dispatch(toggleLeftMenu());
        setLeftMenuOpen(!leftMenuOpen)
    };

    const menuAppliances: MenuCategory[] = [
        {
            icon: 'lamp',
            title: t('leftMenu.lighting.lighting'),
            items: [
                { title: t(`leftMenu.lighting.generic`), icon: 'controllers/black/5K', type: 'lighting'},
                { title: t(`leftMenu.lighting.ceiling_light`), icon: 'controllers/black/4C', type: 'lighting'},
                { title: t(`leftMenu.lighting.lamp`), icon: 'controllers/black/4D', type: 'lighting'},
                { title: t('leftMenu.lighting.spotlight'), icon: 'controllers/black/4F', type: 'lighting'}
            ]
        },
        {
            icon: 'blinds',
            title: t('leftMenu.curtains.curtains'),
            items: [
                { title: t('leftMenu.curtains.curtain'), icon: 'controllers/black/6F', type: 'curtains'},
                { title: t('leftMenu.curtains.shutter'), icon: 'controllers/black/6A', type: 'curtains'},
                { title: t('leftMenu.curtains.blinds'), icon: 'controllers/black/7C', type: 'curtains'},
            ]
        },
        {
            icon: 'icon_heating_cooling',
            title: t('leftMenu.hvac.hvac'),
            items: [
                { title: t('leftMenu.hvac.fancoils'), icon: 'controllers/black/icon_fancoil', type: 'hvac'},
                { title: t('leftMenu.hvac.electric'), icon: 'controllers/black/11B', type: 'hvac'},
                { title: t('leftMenu.hvac.radiator'), icon: 'controllers/black/11A', type: 'hvac'},
                { title: t('leftMenu.hvac.vrv/vrf'), icon: 'controllers/black/11K', type: 'hvac'},
            ]
        }
    ];
    const menuControllers: CategoryItem = {
        icon: 'controller',
        title: t('leftMenu.controllers.controllers'),
        type: 'item',
        items: [
            { title: t('leftMenu.controllers.add'), icon: 'controller', type: 'controller'},
        ]
    } 
    const menuDetectors: MenuCategory = {
        icon: 'icon_detectors_active',
        title: t('leftMenu.detectors.detectors'),
        items: detectors.map((detector) => ({
            title: detector.name,
            icon: 'detectors/' + detector.refImg.replace('.png', ''),
            type: detector.type
        }))
    }
    const switchingActuatorsMenu: MenuCategory = {
        icon: 'switching_actuator_box_blue',
        title: t('leftMenu.actuators.switching'),
        items: switchingActuators.map((actuator) => ({
            title: actuator.name,
            icon: actuator.icon,
            type: actuator.type,
            dinrail: actuator.dinrail
        }))
    }
    const dimmingActuatorsMenu: MenuCategory = {
        icon: 'dimmers_actuator_box_blue',
        title: t('leftMenu.actuators.dimming'),
        items: dimmingActuators.map((actuator) => ({
            title: actuator.name,
            icon: actuator.icon,
            type: actuator.type,
            dinrail: actuator.dinrail
        }))
    }
    const curtainActuatorsMenu: MenuCategory = {
        icon: 'actuator_curtain_box_blue',
        title: t('leftMenu.actuators.curtains'),
        items: curtainActuators.map((actuator) => ({
            title: actuator.name,
            icon: actuator.icon,
            type: actuator.type,
            dinrail: actuator.dinrail
        }))
    }
    const hvacActuatorsMenu: MenuCategory = {
        icon: 'rfatv_blue',
        title: t('leftMenu.actuators.hvac'),
        items: hvacActuators.map((actuator) => ({
            title: actuator.name,
            icon: actuator.icon,
            type: actuator.type,
            dinrail: actuator.dinrail
        }))
    }

    return (
        <LeftMenu leftMenuOpen={leftMenuOpen} onClick={handleMenuClick}>
            <h5 className={leftMenuOpen ? styles.leftMenu__heading__open : styles.leftMenu__heading__closed}>{t('leftMenu.appliances')}</h5>
            {menuAppliances.map((item, index) => (
                <LeftMenu.Category key={index} item={item} leftMenuOpen={leftMenuOpen} categoryId={`appliance-${index}`}/>    
            ))}
            <LeftMenu.Divider />
            <h5 className={leftMenuOpen ? styles.leftMenu__heading__open : styles.leftMenu__heading__closed}>{t('leftMenu.controllers.controllers')}</h5>
            <LeftMenu.Category key="controllers" item={menuControllers} leftMenuOpen={leftMenuOpen} categoryId="controllers"/>    
            <LeftMenu.Category key="detectors" item={menuDetectors} leftMenuOpen={leftMenuOpen} categoryId="detectors"/>
            <LeftMenu.Divider />
            <h5 className={leftMenuOpen ? styles.leftMenu__heading__open : styles.leftMenu__heading__closed}>{t('leftMenu.actuators.heading')}</h5>
            <LeftMenu.CategoryActuators key="actuators-switching" item={switchingActuatorsMenu} leftMenuOpen={leftMenuOpen} categoryId="actuators-switching"/>
            <LeftMenu.CategoryActuators key="actuators-dimming" item={dimmingActuatorsMenu} leftMenuOpen={leftMenuOpen} categoryId="actuators-dimming"/>
            <LeftMenu.CategoryActuators key="actuators-curtains" item={curtainActuatorsMenu} leftMenuOpen={leftMenuOpen} categoryId="actuators-curtains"/>
            <LeftMenu.CategoryActuators key="actuators-hvac" item={hvacActuatorsMenu} leftMenuOpen={leftMenuOpen} categoryId="actuators-hvac"/>
        </LeftMenu>
    );
};

export default LeftMenuComponent;